import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    LinearProgress,
    MenuItem,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faCloudArrowDown, faPenToSquare, faPlus, faRotateRight, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { useApplicationContext } from 'ApplicationContext';
import Title from 'Components/Title';
import {
    ArtikelRefDto,
    ArticleCreate,
    EUserGroup,
    OptionDto,
    ParameterDto,
    PriceTier,
    RessourceRefDto,
} from 'apiClient/Api';
import {
    ArticleView,
    CalculatorApi,
    DeleteBlocked,
    IntegrityIssue,
    RequirementView,
    Rubric,
    useCalculatorApi,
} from './calculatorApi';
import { ArticleImagesButton, ListEditor, StaffelEditor } from './editorParts';
import { CalculatorGitContent } from './CalculatorGit/CalculatorGit';

const ISSUES_DONE_KEY = 'ok-issues-done';
const CAT_LABEL: Record<string, string> = {
    Reference: 'Referenzen',
    NullPreis: 'Nullpreis trotz Referenz',
    ArtikelVerzeichnis: 'Kein Artikelverzeichnis',
    Leistungsbeschreibung: 'Keine Leistungsbeschreibung',
};
const CAT_ORDER = ['Reference', 'NullPreis', 'ArtikelVerzeichnis', 'Leistungsbeschreibung'];

const conflictMessage = (e: unknown): string | null => {
    const data = (e as AxiosError)?.response?.data as DeleteBlocked | undefined;
    if (data?.referencedBy?.length) return `${data.id} wird referenziert von: ${data.referencedBy.join(', ')}`;
    return null;
};

// Anforderungen werden für die „Verwendet in"-Anzeige im Artikel-Dialog einmal pro Session geladen
// und gecacht (rein visuell; reload() im Anforderungen-Tab leert den Cache).
let requirementsCache: Promise<RequirementView[]> | null = null;
const loadRequirementsCached = (api: CalculatorApi): Promise<RequirementView[]> => {
    if (!requirementsCache) requirementsCache = api.listRequirements();
    return requirementsCache;
};
const invalidateRequirementsCache = () => {
    requirementsCache = null;
};
const referencesArticle = (r: RequirementView, aNr: string): boolean =>
    r.articleRefs.some((x) => x.aNr === aNr) ||
    r.resourceRefs.some((rr) => (rr.articleRefs || []).some((x) => x.aNr === aNr));

export interface RefOption {
    id: string;
    label: string;
}

/**
 * Autocomplete für eine Referenz (Anforderung oder Artikel): zeigt „id – Titel/Beschreibung",
 * erlaubt aber per freeSolo auch unbekannte/manuelle IDs (Bestandsdaten). Liefert die reine ID zurück.
 */
const RefAutocomplete: React.FC<{
    value: string;
    options: RefOption[];
    placeholder: string;
    onChange: (id: string) => void;
    minWidth?: number;
}> = ({ value, options, placeholder, onChange, minWidth = 280 }) => {
    const selected = options.find((o) => o.id === value) ?? (value ? { id: value, label: value } : null);
    return (
        <Autocomplete<RefOption, false, false, true>
            freeSolo
            size="small"
            sx={{ minWidth, flexGrow: 1 }}
            options={options}
            value={selected}
            getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
            isOptionEqualToValue={(o, v) => o.id === (typeof v === 'string' ? v : v.id)}
            onChange={(_, v) => onChange(v == null ? '' : typeof v === 'string' ? v : v.id)}
            onInputChange={(_, v, reason) => {
                if (reason === 'input') onChange(v);
            }}
            renderInput={(params) => <TextField {...params} placeholder={placeholder} />}
        />
    );
};

// ==================== Katalog ====================

const ArticleEditDialog: React.FC<{
    api: CalculatorApi;
    article: ArticleView;
    rubrics: Rubric[];
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    onError: (e: unknown) => void;
}> = ({ api, article, rubrics, open, onClose, onSaved, onError }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [draft, setDraft] = useState(article);
    const [staffel, setStaffel] = useState<PriceTier[]>(article.staffel || []);
    const [dirty, setDirty] = useState(false);
    const [usedIn, setUsedIn] = useState<RequirementView[] | null>(null);

    useEffect(() => {
        setDraft(article);
        setStaffel(article.staffel || []);
        setDirty(false);
    }, [article]);

    // Visuell: welche Anforderungen referenzieren diesen Artikel?
    useEffect(() => {
        if (!open) return;
        let active = true;
        setUsedIn(null);
        loadRequirementsCached(api)
            .then((reqs) => active && setUsedIn(reqs.filter((r) => referencesArticle(r, article.articleNumber))))
            .catch(() => active && setUsedIn([]));
        return () => {
            active = false;
        };
    }, [open, api, article.articleNumber]);

    const patch = (p: Partial<ArticleView>) => {
        setDraft((d) => ({ ...d, ...p }));
        setDirty(true);
    };

    const save = async () => {
        try {
            await api.updateArticle(article.articleNumber, {
                beschreibung: draft.beschreibung,
                vkf1: draft.vkf1,
                netto1: draft.netto1,
                lohnNetto: draft.lohnNetto,
                lohnMinuten: draft.lohnMinuten,
                articleGroup: draft.articleGroup !== article.articleGroup ? draft.articleGroup : null,
                nullpreisOk: draft.nullpreisOk,
                lvOff: draft.lvOff,
                dirOff: draft.dirOff,
                staffel,
            });
            enqueueSnackbar(`Gespeichert: ${article.articleNumber}`, { variant: 'success' });
            onSaved();
            onClose();
        } catch (e) {
            onError(e);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Artikel ${article.articleNumber} löschen?`)) return;
        try {
            await api.deleteArticle(article.articleNumber);
            enqueueSnackbar(`Gelöscht: ${article.articleNumber}`, { variant: 'success' });
            onSaved();
            onClose();
        } catch (e) {
            const msg = conflictMessage(e);
            if (msg) enqueueSnackbar(msg, { variant: 'warning' });
            else onError(e);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                    {article.articleNumber}
                    {article.missingArticleDir && <Chip size="small" label="Verz" sx={{ ml: 1 }} />}
                    {article.missingLeistung && <Chip size="small" label="LV" sx={{ ml: 0.5 }} />}
                </Box>
                <ArticleImagesButton api={api} articleNumber={article.articleNumber} />
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
                        <TextField
                            select
                            size="small"
                            label="Rubrik (Gr.)"
                            value={draft.articleGroup || ''}
                            onChange={(e) => patch({ articleGroup: e.target.value })}
                            sx={{ minWidth: 160 }}
                        >
                            <MenuItem value="">(–)</MenuItem>
                            {rubrics.map((r) => (
                                <MenuItem key={r.code} value={r.code}>
                                    {r.code} – {r.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            size="small"
                            label="Typ"
                            value={article.typ}
                            slotProps={{ input: { readOnly: true } }}
                            sx={{ width: 120 }}
                        />
                    </Stack>
                    <TextField
                        size="small"
                        label="Beschreibung"
                        fullWidth
                        multiline
                        value={draft.beschreibung || ''}
                        onChange={(e) => patch({ beschreibung: e.target.value })}
                    />
                    <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
                        {(
                            [
                                ['vkf1', 'vkf1'],
                                ['netto1', 'netto1'],
                                ['lohnNetto', 'Lohn €'],
                                ['lohnMinuten', 'Lohn min'],
                            ] as const
                        ).map(([field, label]) => (
                            <TextField
                                key={field}
                                type="number"
                                size="small"
                                label={label}
                                value={draft[field] ?? 0}
                                onChange={(e) => patch({ [field]: Number(e.target.value) || 0 } as Partial<ArticleView>)}
                                slotProps={{ htmlInput: { step: 0.01 } }}
                                sx={{ width: 140 }}
                            />
                        ))}
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                        <FormControlLabel
                            control={<Checkbox checked={draft.nullpreisOk} onChange={(e) => patch({ nullpreisOk: e.target.checked })} />}
                            label="Nullpreis ok (NP)"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={draft.lvOff} onChange={(e) => patch({ lvOff: e.target.checked })} />}
                            label="ohne LV"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={draft.dirOff} onChange={(e) => patch({ dirOff: e.target.checked })} />}
                            label="ohne Verzeichnis (Dir)"
                        />
                    </Stack>
                    <StaffelEditor
                        staffel={staffel}
                        onChange={(next) => {
                            setStaffel(next);
                            setDirty(true);
                        }}
                    />

                    <Divider />
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Verwendet in Anforderungen
                            {usedIn ? ` (${usedIn.length})` : ''}
                        </Typography>
                        {usedIn === null ? (
                            <LinearProgress />
                        ) : usedIn.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Dieser Artikel wird von keiner Anforderung referenziert.
                            </Typography>
                        ) : (
                            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }} useFlexGap>
                                {usedIn.map((r) => (
                                    <Tooltip key={r.id} title={`${r.category} · ${r.titel}`}>
                                        <Chip size="small" label={`${r.id} – ${r.titel}`} />
                                    </Tooltip>
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
                <Button color="error" startIcon={<FontAwesomeIcon icon={faTrash} />} onClick={remove}>
                    Löschen
                </Button>
                <Box>
                    <Button onClick={onClose}>Abbrechen</Button>
                    <Button variant="contained" disabled={!dirty} onClick={save} sx={{ ml: 1 }}>
                        Speichern
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

const NewArticleForm: React.FC<{ api: CalculatorApi; groups: string[]; rubrics: Rubric[]; onCreated: () => void; onError: (e: unknown) => void }> = ({
    api,
    groups,
    rubrics,
    onCreated,
    onError,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [form, setForm] = useState<ArticleCreate>({ articleNumber: '', groupTitle: '', articleGroup: 'B', typ: 'intern', catalogs: '' });

    const create = async () => {
        if (!form.articleNumber) {
            enqueueSnackbar('Artikel-Nr fehlt.', { variant: 'warning' });
            return;
        }
        try {
            await api.createArticle(form);
            enqueueSnackbar(`Angelegt: ${form.articleNumber}`, { variant: 'success' });
            setForm({ articleNumber: '', groupTitle: '', articleGroup: 'B', typ: 'intern', catalogs: '' });
            onCreated();
        } catch (e) {
            onError(e);
        }
    };

    return (
        <Accordion>
            <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
                <Typography>Neuen Artikel anlegen</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                    <TextField size="small" label="Artikel-Nr" value={form.articleNumber} onChange={(e) => setForm({ ...form, articleNumber: e.target.value })} />
                    <TextField size="small" label="Gruppen-Titel" value={form.groupTitle} onChange={(e) => setForm({ ...form, groupTitle: e.target.value })} select sx={{ minWidth: 160 }}>
                        <MenuItem value="">(letzte Gruppe)</MenuItem>
                        {groups.map((g) => (
                            <MenuItem key={g} value={g}>
                                {g}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField size="small" label="a_gruppe" value={form.articleGroup} onChange={(e) => setForm({ ...form, articleGroup: e.target.value })} select sx={{ minWidth: 90 }}>
                        {rubrics.map((r) => (
                            <MenuItem key={r.code} value={r.code}>
                                {r.code}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField size="small" label="Typ" value={form.typ} onChange={(e) => setForm({ ...form, typ: e.target.value })} />
                    <TextField size="small" label="Beschreibung" value={form.beschreibung || ''} onChange={(e) => setForm({ ...form, beschreibung: e.target.value })} sx={{ minWidth: 220 }} />
                    <TextField size="small" type="number" label="vkf1" value={form.vkf1 ?? 0} onChange={(e) => setForm({ ...form, vkf1: Number(e.target.value) || 0 })} />
                    <TextField size="small" type="number" label="lohnNetto" value={form.lohnNetto ?? 0} onChange={(e) => setForm({ ...form, lohnNetto: Number(e.target.value) || 0 })} />
                    <Button variant="contained" startIcon={<FontAwesomeIcon icon={faPlus} />} onClick={create}>
                        Anlegen
                    </Button>
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};

const flagChips = (a: ArticleView) => {
    const flags: string[] = [];
    if (a.nullpreisOk) flags.push('NP');
    if (a.lvOff) flags.push('LV');
    if (a.dirOff) flags.push('Dir');
    return flags;
};

const CatalogTab: React.FC<{ api: CalculatorApi; rubrics: Rubric[]; onError: (e: unknown) => void }> = ({ api, rubrics, onError }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [articles, setArticles] = useState<ArticleView[]>([]);
    const [groups, setGroups] = useState<string[]>([]);
    const [search, setSearch] = useState('');
    const [onlyMissing, setOnlyMissing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState<ArticleView | null>(null);

    const reload = useCallback(() => {
        setLoading(true);
        Promise.all([api.listArticles(), api.listArticleGroups()])
            .then(([a, g]) => {
                setArticles(a);
                setGroups(g);
            })
            .catch(onError)
            .finally(() => setLoading(false));
    }, [api, onError]);

    useEffect(() => reload(), [reload]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return articles.filter((a) => {
            if (onlyMissing && !a.priceMissing) return false;
            if (!q) return true;
            return a.articleNumber.toLowerCase().includes(q) || (a.beschreibung || '').toLowerCase().includes(q);
        });
    }, [articles, search, onlyMissing]);

    const remove = async (a: ArticleView) => {
        if (!window.confirm(`Artikel ${a.articleNumber} löschen?`)) return;
        try {
            await api.deleteArticle(a.articleNumber);
            enqueueSnackbar(`Gelöscht: ${a.articleNumber}`, { variant: 'success' });
            reload();
        } catch (e) {
            const msg = conflictMessage(e);
            if (msg) enqueueSnackbar(msg, { variant: 'warning' });
            else onError(e);
        }
    };

    const money = (v?: number) => Number(v ?? 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const columns: GridColDef<ArticleView>[] = [
        { field: 'articleNumber', headerName: 'Artikel-Nr', width: 130, renderCell: (p) => <span style={{ fontFamily: 'monospace' }}>{p.value}</span> },
        { field: 'articleGroup', headerName: 'Gr.', width: 70 },
        { field: 'typ', headerName: 'Typ', width: 90 },
        { field: 'beschreibung', headerName: 'Beschreibung', flex: 1, minWidth: 220 },
        { field: 'vkf1', headerName: 'vkf1', width: 90, type: 'number', valueFormatter: (v: number) => money(v) },
        { field: 'netto1', headerName: 'netto1', width: 90, type: 'number', valueFormatter: (v: number) => money(v) },
        { field: 'lohnNetto', headerName: 'Lohn €', width: 90, type: 'number', valueFormatter: (v: number) => money(v) },
        { field: 'lohnMinuten', headerName: 'Lohn min', width: 90, type: 'number' },
        {
            field: 'flags',
            headerName: 'Flags',
            width: 130,
            sortable: false,
            filterable: false,
            renderCell: (p) => (
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', height: '100%' }}>
                    {flagChips(p.row).map((f) => (
                        <Chip key={f} size="small" label={f} />
                    ))}
                </Stack>
            ),
        },
        {
            field: 'actions',
            type: 'actions',
            width: 90,
            getActions: (params: GridRowParams<ArticleView>) => [
                <GridActionsCellItem
                    key="edit"
                    icon={<FontAwesomeIcon icon={faPenToSquare} />}
                    label="Bearbeiten"
                    onClick={() => setEditing(params.row)}
                    color="inherit"
                />,
                <GridActionsCellItem
                    key="delete"
                    icon={
                        <Box component="span" sx={{ color: 'error.main' }}>
                            <FontAwesomeIcon icon={faTrash} />
                        </Box>
                    }
                    label="Löschen"
                    onClick={() => remove(params.row)}
                    color="inherit"
                />,
            ],
        },
    ];

    return (
        <Box>
            <Stack direction="row" spacing={2} useFlexGap sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField size="small" label="Suche" value={search} onChange={(e) => setSearch(e.target.value)} />
                <FormControlLabel control={<Checkbox checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} />} label="Nur fehlende Preise" />
                <Typography color="text.secondary">{articles.length} Artikel</Typography>
            </Stack>
            <NewArticleForm api={api} groups={groups} rubrics={rubrics} onCreated={reload} onError={onError} />
            <DataGrid<ArticleView>
                rows={filtered}
                columns={columns}
                loading={loading}
                getRowId={(r) => r.articleNumber}
                onRowDoubleClick={(p) => setEditing(p.row as ArticleView)}
                getRowClassName={(p) => ((p.row as ArticleView).priceMissing ? 'row-missing' : '')}
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                sx={{
                    mt: 1,
                    '& .row-missing': { bgcolor: 'error.light' },
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                }}
            />
            {editing && (
                <ArticleEditDialog
                    api={api}
                    article={editing}
                    rubrics={rubrics}
                    open={!!editing}
                    onClose={() => setEditing(null)}
                    onSaved={reload}
                    onError={onError}
                />
            )}
        </Box>
    );
};

// ==================== Anforderungen ====================

const RequirementEditDialog: React.FC<{
    api: CalculatorApi;
    view: RequirementView;
    rubrics: Rubric[];
    reqOptions: RefOption[];
    artOptions: RefOption[];
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    onError: (e: unknown) => void;
}> = ({ api, view, rubrics, reqOptions, artOptions, open, onClose, onSaved, onError }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [articleGroup, setArticleGroup] = useState(view.articleGroup);
    const [titel, setTitel] = useState(view.titel);
    const [details, setDetails] = useState(view.details || '');
    const [eingabe, setEingabe] = useState(view.eingabe || '');
    const [implicit, setImplicit] = useState(view.implicit);
    const [parameters, setParameters] = useState<ParameterDto[]>(view.parameters.map((p) => ({ ...p })));
    const [articleRefs, setArticleRefs] = useState<ArtikelRefDto[]>(view.articleRefs.map((r) => ({ ...r })));
    const [resourceRefs, setResourceRefs] = useState<RessourceRefDto[]>(
        view.resourceRefs.map((r) => ({ ...r, articleRefs: (r.articleRefs || []).map((x) => ({ ...x })) })),
    );
    const [options, setOptions] = useState<OptionDto[]>(view.options.map((o) => ({ ...o })));

    const save = async () => {
        if (!titel) {
            enqueueSnackbar('Titel fehlt.', { variant: 'warning' });
            return;
        }
        try {
            await api.updateRequirement(view.id, {
                articleGroup,
                titel,
                details: details || null,
                eingabe: eingabe || null,
                implicit,
                parameters: parameters.filter((p) => p.key),
                articleRefs: articleRefs.filter((r) => r.aNr || r.gId),
                resourceRefs: resourceRefs.filter((r) => r.name),
                options: options.filter((o) => o.anforderungId),
            });
            enqueueSnackbar(`Gespeichert: ${view.id}`, { variant: 'success' });
            onSaved();
            onClose();
        } catch (e) {
            onError(e);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Anforderung ${view.id} löschen?`)) return;
        try {
            await api.deleteRequirement(view.id);
            enqueueSnackbar(`Gelöscht: ${view.id}`, { variant: 'success' });
            onSaved();
            onClose();
        } catch (e) {
            const msg = conflictMessage(e);
            if (msg) enqueueSnackbar(msg, { variant: 'warning' });
            else onError(e);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box component="span" sx={{ fontFamily: 'monospace', mr: 1 }}>
                    {view.id}
                </Box>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                    {view.titel}
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                        <TextField select size="small" label="Rubrik" value={articleGroup} onChange={(e) => setArticleGroup(e.target.value)} sx={{ minWidth: 120 }}>
                            <MenuItem value="">(–)</MenuItem>
                            {rubrics.map((r) => (
                                <MenuItem key={r.code} value={r.code}>
                                    {r.code} – {r.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField size="small" label="Titel" value={titel} onChange={(e) => setTitel(e.target.value)} sx={{ flexGrow: 1, minWidth: 200 }} />
                    </Stack>
                    <TextField size="small" label="Details" value={details} onChange={(e) => setDetails(e.target.value)} fullWidth />
                    <TextField size="small" label="Eingabe-Label" value={eingabe} onChange={(e) => setEingabe(e.target.value)} />
                    <FormControlLabel control={<Checkbox checked={implicit} onChange={(e) => setImplicit(e.target.checked)} />} label="implizit (nur als Option sichtbar)" />

                    <ListEditor<ParameterDto>
                        title="Parameter"
                        items={parameters}
                        onChange={setParameters}
                        makeEmpty={() => ({ key: '', value: '', type: null })}
                        renderRow={(it, update) => (
                            <>
                                <TextField size="small" placeholder="key" value={it.key || ''} onChange={(e) => update({ key: e.target.value })} />
                                <TextField size="small" placeholder="value" value={it.value || ''} onChange={(e) => update({ value: e.target.value })} />
                                <TextField size="small" placeholder="type (opt.)" value={it.type || ''} onChange={(e) => update({ type: e.target.value || null })} />
                            </>
                        )}
                    />

                    <ListEditor<ArtikelRefDto>
                        title="artikel_ref (bedingungen)"
                        items={articleRefs}
                        onChange={setArticleRefs}
                        makeEmpty={() => ({ aNr: '', gId: '' })}
                        renderRow={(it, update) => (
                            <>
                                <RefAutocomplete
                                    value={it.aNr || ''}
                                    options={artOptions}
                                    placeholder="a_nr"
                                    onChange={(id) => update({ aNr: id || null })}
                                />
                                <TextField size="small" placeholder="g_id (alt.)" value={it.gId || ''} onChange={(e) => update({ gId: e.target.value || null })} />
                            </>
                        )}
                    />

                    <ListEditor<RessourceRefDto>
                        title="ressource_ref (bedingungen)"
                        items={resourceRefs}
                        onChange={setResourceRefs}
                        makeEmpty={() => ({ name: '', anzahl: 1, per: null, articleRefs: [] })}
                        renderRow={(it, update) => (
                            <>
                                <TextField size="small" placeholder="name" value={it.name || ''} onChange={(e) => update({ name: e.target.value })} />
                                <TextField size="small" type="number" placeholder="anzahl" value={it.anzahl ?? 0} onChange={(e) => update({ anzahl: Number(e.target.value) || 0 })} sx={{ width: 90 }} />
                                <TextField size="small" type="number" placeholder="per" value={it.per ?? ''} onChange={(e) => update({ per: e.target.value === '' ? null : Number(e.target.value) })} sx={{ width: 90 }} />
                            </>
                        )}
                    />

                    <ListEditor<OptionDto>
                        title="Optionen"
                        items={options}
                        onChange={setOptions}
                        makeEmpty={() => ({ anforderungId: '', eingabeTyp: 'checkbox', rGruppe: null, anzahl: null })}
                        renderRow={(it, update) => (
                            <>
                                <RefAutocomplete
                                    value={it.anforderungId || ''}
                                    options={reqOptions}
                                    placeholder="anforderung_id"
                                    onChange={(id) => update({ anforderungId: id })}
                                />
                                <TextField select size="small" value={it.eingabeTyp || 'checkbox'} onChange={(e) => update({ eingabeTyp: e.target.value })}>
                                    {['checkbox', 'spin', 'radio'].map((c) => (
                                        <MenuItem key={c} value={c}>
                                            {c}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField size="small" type="number" placeholder="r_gruppe" value={it.rGruppe ?? ''} onChange={(e) => update({ rGruppe: e.target.value === '' ? null : Number(e.target.value) })} sx={{ width: 100 }} />
                                <TextField size="small" type="number" placeholder="anzahl" value={it.anzahl ?? ''} onChange={(e) => update({ anzahl: e.target.value === '' ? null : Number(e.target.value) })} sx={{ width: 90 }} />
                            </>
                        )}
                    />

                </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
                <Button color="error" startIcon={<FontAwesomeIcon icon={faTrash} />} onClick={remove}>
                    Löschen
                </Button>
                <Box>
                    <Button onClick={onClose}>Abbrechen</Button>
                    <Button variant="contained" onClick={save} sx={{ ml: 1 }}>
                        Speichern
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

/** Kompakte Beziehungs-Chips; der Optionen-Chip löst im Tooltip die referenzierten Anforderungen auf. */
const RelationChips: React.FC<{ view: RequirementView; reqTitle: (id: string) => string | undefined }> = ({ view, reqTitle }) => {
    const optList =
        view.options
            .map((o) => {
                const t = reqTitle(o.anforderungId);
                return t ? `${o.anforderungId} – ${t}` : o.anforderungId;
            })
            .join('\n') || '';
    return (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', height: '100%', flexWrap: 'wrap' }}>
            {view.parameters.length > 0 && (
                <Tooltip title="Parameter">
                    <Chip size="small" variant="outlined" label={`${view.parameters.length} P`} />
                </Tooltip>
            )}
            {view.articleRefs.length > 0 && (
                <Tooltip title="artikel_ref (Artikel-Bedingungen)">
                    <Chip size="small" color="primary" variant="outlined" label={`${view.articleRefs.length} Art`} />
                </Tooltip>
            )}
            {view.resourceRefs.length > 0 && (
                <Tooltip title="ressource_ref">
                    <Chip size="small" color="secondary" variant="outlined" label={`${view.resourceRefs.length} Res`} />
                </Tooltip>
            )}
            {view.options.length > 0 && (
                <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{optList}</Box>}>
                    <Chip size="small" color="info" variant="outlined" label={`${view.options.length} Opt`} />
                </Tooltip>
            )}
        </Stack>
    );
};

const RequirementsTab: React.FC<{ api: CalculatorApi; rubrics: Rubric[]; onError: (e: unknown) => void }> = ({ api, rubrics, onError }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [requirements, setRequirements] = useState<RequirementView[]>([]);
    const [articles, setArticles] = useState<ArticleView[]>([]);
    const [search, setSearch] = useState('');
    const [rubricFilter, setRubricFilter] = useState('');
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState<RequirementView | null>(null);

    const reload = useCallback(() => {
        setLoading(true);
        invalidateRequirementsCache();
        Promise.all([api.listRequirements(), api.listArticles()])
            .then(([reqs, arts]) => {
                setRequirements(reqs);
                setArticles(arts);
            })
            .catch(onError)
            .finally(() => setLoading(false));
    }, [api, onError]);

    useEffect(() => reload(), [reload]);

    // Referenz-Auflösung (rein clientseitig aus den geladenen Listen).
    const reqTitleById = useMemo(() => {
        const m = new Map<string, string>();
        for (const r of requirements) m.set(r.id, r.titel || '');
        return m;
    }, [requirements]);
    const reqOptions = useMemo<RefOption[]>(
        () => requirements.map((r) => ({ id: r.id, label: `${r.id} – ${r.titel || ''}` })),
        [requirements],
    );
    const artOptions = useMemo<RefOption[]>(
        () => articles.map((a) => ({ id: a.articleNumber, label: `${a.articleNumber} – ${a.beschreibung || ''}` })),
        [articles],
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return requirements.filter(
            (r) =>
                (!rubricFilter || r.articleGroup === rubricFilter) &&
                (!q || r.id.toLowerCase().includes(q) || (r.titel || '').toLowerCase().includes(q)),
        );
    }, [requirements, search, rubricFilter]);

    const remove = async (r: RequirementView) => {
        if (!window.confirm(`Anforderung ${r.id} löschen?`)) return;
        try {
            await api.deleteRequirement(r.id);
            enqueueSnackbar(`Gelöscht: ${r.id}`, { variant: 'success' });
            reload();
        } catch (e) {
            const msg = conflictMessage(e);
            if (msg) enqueueSnackbar(msg, { variant: 'warning' });
            else onError(e);
        }
    };

    const columns: GridColDef<RequirementView>[] = [
        { field: 'id', headerName: 'ID', width: 150, renderCell: (p) => <span style={{ fontFamily: 'monospace' }}>{p.value}</span> },
        { field: 'category', headerName: 'Kategorie', width: 150 },
        { field: 'articleGroup', headerName: 'Rubrik', width: 90 },
        { field: 'titel', headerName: 'Titel', flex: 1, minWidth: 220 },
        {
            field: 'implicit',
            headerName: 'Implizit',
            width: 90,
            sortable: false,
            renderCell: (p) =>
                p.row.implicit ? (
                    <Stack sx={{ height: '100%', justifyContent: 'center' }}>
                        <Chip size="small" label="implizit" />
                    </Stack>
                ) : null,
        },
        {
            field: 'relations',
            headerName: 'Beziehungen',
            width: 230,
            sortable: false,
            filterable: false,
            renderCell: (p) => <RelationChips view={p.row} reqTitle={(id) => reqTitleById.get(id)} />,
        },
        {
            field: 'actions',
            type: 'actions',
            width: 90,
            getActions: (params: GridRowParams<RequirementView>) => [
                <GridActionsCellItem key="edit" icon={<FontAwesomeIcon icon={faPenToSquare} />} label="Bearbeiten" onClick={() => setEditing(params.row)} color="inherit" />,
                <GridActionsCellItem
                    key="delete"
                    icon={
                        <Box component="span" sx={{ color: 'error.main' }}>
                            <FontAwesomeIcon icon={faTrash} />
                        </Box>
                    }
                    label="Löschen"
                    onClick={() => remove(params.row)}
                    color="inherit"
                />,
            ],
        },
    ];

    return (
        <Box>
            <Stack direction="row" spacing={2} useFlexGap sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField size="small" label="Suche" value={search} onChange={(e) => setSearch(e.target.value)} />
                <TextField select size="small" label="Rubrik" value={rubricFilter} onChange={(e) => setRubricFilter(e.target.value)} sx={{ minWidth: 160 }}>
                    <MenuItem value="">Alle</MenuItem>
                    {rubrics.map((r) => (
                        <MenuItem key={r.code} value={r.code}>
                            {r.code} – {r.label}
                        </MenuItem>
                    ))}
                </TextField>
                <Typography color="text.secondary">{requirements.length} Anforderungen</Typography>
            </Stack>
            <DataGrid<RequirementView>
                rows={filtered}
                columns={columns}
                loading={loading}
                getRowId={(r) => r.id}
                onRowDoubleClick={(p) => setEditing(p.row as RequirementView)}
                pageSizeOptions={[25, 50, 100]}
                initialState={{
                    pagination: { paginationModel: { pageSize: 25 } },
                    sorting: { sortModel: [{ field: 'category', sort: 'asc' }] },
                }}
                sx={{ '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' } }}
            />
            {editing && (
                <RequirementEditDialog
                    api={api}
                    view={editing}
                    rubrics={rubrics}
                    reqOptions={reqOptions}
                    artOptions={artOptions}
                    open={!!editing}
                    onClose={() => setEditing(null)}
                    onSaved={reload}
                    onError={onError}
                />
            )}
        </Box>
    );
};

// ==================== Validierung ====================

const ValidationTab: React.FC<{ api: CalculatorApi; onError: (e: unknown) => void }> = ({ api, onError }) => {
    const [issues, setIssues] = useState<IntegrityIssue[]>([]);
    const [loading, setLoading] = useState(true);
    const [hideDone, setHideDone] = useState(false);
    const [done, setDone] = useState<Set<string>>(
        () => new Set(JSON.parse(localStorage.getItem(ISSUES_DONE_KEY) || '[]')),
    );

    const reload = useCallback(() => {
        setLoading(true);
        api.validate().then(setIssues).catch(onError).finally(() => setLoading(false));
    }, [api, onError]);

    useEffect(() => reload(), [reload]);

    const key = (i: IntegrityIssue) => `${i.severity}|${i.category || ''}|${i.where}|${i.message}`;
    const toggleDone = (i: IntegrityIssue, checked: boolean) => {
        const next = new Set(done);
        if (checked) next.add(key(i));
        else next.delete(key(i));
        setDone(next);
        localStorage.setItem(ISSUES_DONE_KEY, JSON.stringify(Array.from(next)));
    };

    const errors = issues.filter((i) => i.severity === 'Error').length;
    const warnings = issues.length - errors;

    const byCategory = useMemo(() => {
        const map = new Map<string, IntegrityIssue[]>();
        for (const i of issues) {
            const c = i.category || 'Sonstiges';
            if (!map.has(c)) map.set(c, []);
            map.get(c)!.push(i);
        }
        return Array.from(map.entries()).sort(
            (a, b) => (CAT_ORDER.indexOf(a[0]) + 1 || 99) - (CAT_ORDER.indexOf(b[0]) + 1 || 99),
        );
    }, [issues]);

    return (
        <Box>
            <Stack direction="row" spacing={2} useFlexGap sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="outlined" onClick={reload}>
                    Neu prüfen
                </Button>
                <FormControlLabel control={<Checkbox checked={hideDone} onChange={(e) => setHideDone(e.target.checked)} />} label="Erledigte ausblenden" />
                <Typography color="text.secondary">
                    {errors} Fehler, {warnings} Warnungen
                </Typography>
            </Stack>
            {loading && <LinearProgress sx={{ my: 1 }} />}
            {issues.length === 0 && !loading && <Alert severity="success">Keine Befunde – alle Referenzen aufgelöst.</Alert>}
            {byCategory.map(([category, items]) => {
                const visible = hideDone ? items.filter((i) => !done.has(key(i))) : items;
                if (!visible.length) return null;
                return (
                    <Accordion key={category} defaultExpanded={visible.some((i) => i.severity === 'Error')}>
                        <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
                            <Typography>
                                {CAT_LABEL[category] || category} ({visible.length}/{items.length})
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {visible.map((i, idx) => (
                                <Stack
                                    key={idx}
                                    direction="row"
                                    spacing={1}
                                    sx={{ alignItems: 'center', py: 0.5, opacity: done.has(key(i)) ? 0.5 : 1 }}
                                >
                                    <Checkbox size="small" checked={done.has(key(i))} onChange={(e) => toggleDone(i, e.target.checked)} />
                                    <Chip size="small" color={i.severity === 'Error' ? 'error' : 'warning'} label={i.severity} />
                                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{i.where}</Typography>
                                    <Typography>{i.message}</Typography>
                                </Stack>
                            ))}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};

// ==================== Seite ====================

export const MasterDataEditor: React.FC = () => {
    const { t } = useTranslation();
    const { setTitle, apiError, userData } = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();
    const api = useCalculatorApi();
    const [tab, setTab] = useState(0);
    const [rubrics, setRubrics] = useState<Rubric[]>([]);
    const [reloadNonce, setReloadNonce] = useState(0);
    const [reloading, setReloading] = useState(false);
    const [syncing, setSyncing] = useState(false);

    // Git-Steuerung ist Admin+ (deckungsgleich mit [Authorize(ADMIN)] im Backend).
    const canManageGit = (userData?.userGroup ?? EUserGroup.CUSTOMER) <= EUserGroup.ADMIN;

    useEffect(() => setTitle(t('masterData.title')), [setTitle, t]);
    useEffect(() => {
        api.listRubrics().then(setRubrics).catch(() => setRubrics([]));
    }, [api, reloadNonce]);

    // Lädt die Stammdaten serverseitig frisch aus den XML-Quellen und frischt die Ansicht.
    const handleReload = async () => {
        setReloading(true);
        try {
            await api.reloadData();
            invalidateRequirementsCache();
            setReloadNonce((n) => n + 1);
            enqueueSnackbar(t('masterData.reloaded'), { variant: 'success' });
        } catch (e) {
            apiError(e);
        } finally {
            setReloading(false);
        }
    };

    // Lädt die Preise frisch aus LabelWin (Connector-Cache verwerfen). Preise = Stammdaten.
    const handleSyncPrices = async () => {
        setSyncing(true);
        try {
            const r = await api.labelwinSync();
            if (!r.enabled) {
                enqueueSnackbar(t('calculation.labelwin.disabled'), { variant: 'info' });
                return;
            }
            enqueueSnackbar(t('calculation.labelwin.synced', { count: r.count }), { variant: 'success' });
            invalidateRequirementsCache();
            setReloadNonce((n) => n + 1);
        } catch (e) {
            apiError(e);
        } finally {
            setSyncing(false);
        }
    };

    return (
        <Grid size={12}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Title>{t('masterData.title')}</Title>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        startIcon={<FontAwesomeIcon icon={faCloudArrowDown} spin={syncing} />}
                        onClick={handleSyncPrices}
                        disabled={syncing}
                    >
                        {t('calculation.labelwin.sync')}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<FontAwesomeIcon icon={faRotateRight} spin={reloading} />}
                        onClick={handleReload}
                        disabled={reloading}
                    >
                        {t('masterData.reload')}
                    </Button>
                </Stack>
            </Stack>
            <Paper sx={{ p: 2 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                    <Tab label={t('masterData.catalog')} />
                    <Tab label={t('masterData.requirements')} />
                    <Tab label={t('masterData.validation')} />
                    {canManageGit && <Tab label={t('masterData.git')} />}
                </Tabs>
                <Divider sx={{ mb: 2 }} />
                {tab === 0 && <CatalogTab key={`cat-${reloadNonce}`} api={api} rubrics={rubrics} onError={apiError} />}
                {tab === 1 && <RequirementsTab key={`req-${reloadNonce}`} api={api} rubrics={rubrics} onError={apiError} />}
                {tab === 2 && <ValidationTab key={`val-${reloadNonce}`} api={api} onError={apiError} />}
                {tab === 3 && canManageGit && <CalculatorGitContent />}
            </Paper>
        </Grid>
    );
};

export default MasterDataEditor;
